import React, { FC, ReactElement, useState } from 'react'
import { Modal, Icons } from '../../../Shared/Ant'
import { Button, notification, Space } from 'antd'
import { MarkAsReadBulkProps } from '../types'
import './MarkAsReadBulk.modules.scss'
import { MarkAsReadBulkContent } from './MarkAsReadBulkContent'
import { getQueryParamsState } from '../../../../utils/queryParams'
import { Requests, ReactQuery } from '../../../../api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import MESSAGES from './MarkAsReadBulk.messages'

export const MarkAsReadBulk: FC<MarkAsReadBulkProps> = ({ unreadCount }): ReactElement => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const params = getQueryParamsState()
  const owner = params.owner || 'all'
  const updateBulkRequest = Requests.notificationUpdateBulk()
  const queryClient = useQueryClient()

  // Mutation for bulk updating notification read status
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ readStatus }: { readStatus: boolean }) => {
      const response = await ReactQuery.patchResource(updateBulkRequest, { read: readStatus, owner: owner })
      return response.data
    },
    onSuccess: () => {
      // Reset queries to clear stale data and force refetch
      queryClient.resetQueries({ queryKey: ['notifications'] })
      queryClient.resetQueries({ queryKey: ['unreadNotificationsCount'] })
    }
  })

  const handleSuccess = () => {
    const key = `open-${Date.now()}`

    notification.open({
      key: key,
      className: 'mark-all-read-notification',
      message: (
        <Space>
          <span>{MESSAGES.CONFIRM_PROMPT.SUCCESS}</span>
          <Button type="link" onClick={() => handleUndo(key)}>
            {MESSAGES.CONFIRM_PROMPT.UNDO}
          </Button>
        </Space>
      ),
      placement: 'bottomLeft',
      icon: <Icons.CheckCircle className="icon" />,
      closeIcon: (
        <span className="close-icon-wrapper">
          <Icons.Close className="close-icon" />
        </span>
      )
    })
  }

  const handleOk = () => {
    setIsModalVisible(false)

    bulkUpdateMutation.mutate(
      { readStatus: true },
      {
        onSuccess: () => {
          handleSuccess()
        }
      }
    )
  }

  const handleUndo = (key: string) => {
    notification.close(key)

    bulkUpdateMutation.mutate({ readStatus: false })
  }

  return (
    <>
      <Button
        type="link"
        disabled={unreadCount === 0 || bulkUpdateMutation.isLoading}
        loading={bulkUpdateMutation.isLoading}
        className="mark-all-read-button"
        icon={<Icons.Check className="icon" />}
        onClick={() => setIsModalVisible(true)}
      >
        {MESSAGES.ACTION} ({unreadCount})
      </Button>
      <Modal
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        okText={MESSAGES.CONFIRM_PROMPT.OK}
        cancelButtonProps={{ type: 'link' }}
        width={800}
      >
        <MarkAsReadBulkContent unreadCount={unreadCount} owner={owner} />
      </Modal>
    </>
  )
}
